function getOrderBumpVariantIds() {
  const ids = new Set();
  const el = document.getElementById('OrderBumpVariantIds');
  if (el) {
    try {
      JSON.parse(el.textContent).forEach((id) => ids.add(id));
    } catch (_) {}
  }
  return ids;
}

function disableCheckoutButtons() {
  const checkoutButtons = document.querySelectorAll('button[name="checkout"]');
  checkoutButtons.forEach((btn) => {
    btn.disabled = true;
    btn.style.pointerEvents = 'none';
    btn.style.opacity = '0.5';
    btn.style.cursor = 'not-allowed';
  });
}

function enableCheckoutButtons() {
  const checkoutButtons = document.querySelectorAll('button[name="checkout"]');
  checkoutButtons.forEach((btn) => {
    btn.disabled = false;
    btn.style.pointerEvents = '';
    btn.style.opacity = '';
    btn.style.cursor = '';
  });
}

function isBumpItem(item, bumpVariantIds) {
  return (
    bumpVariantIds.has(item.variant_id) ||
    (item.properties && item.properties['_order_bump'] === '1')
  );
}

function buildLineItemPayload(item) {
  const payload = {
    id: item.variant_id,
    quantity: item.quantity,
    properties: item.properties || {},
  };
  if (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan) {
    payload.selling_plan = item.selling_plan_allocation.selling_plan.id;
  }
  return payload;
}

function fetchCart() {
  return fetch('/cart.js', {
    headers: { 'Content-Type': 'application/json' },
  }).then((r) => r.json());
}

function cartUpdate(body) {
  return fetch('/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  }).then((r) => r.json());
}

function cartAdd(body) {
  return fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
    body: JSON.stringify(body),
  }).then((r) => r.json());
}

/**
 * Rebuilds the entire cart so that non-option items come first,
 * then option items last. This ensures checkout order is correct.
 *
 * @param {number} newBumpVariantId  - variant ID being added (0 if none)
 * @param {Set}    bumpVariantIds    - all configured bump variant IDs
 * @returns {object} last /cart/add.js response (with sections)
 */
async function rebuildCart(newBumpVariantId, bumpVariantIds) {
  const cart = await fetchCart();
  const items = cart.items || [];

  const normalItems = items.filter((item) => !isBumpItem(item, bumpVariantIds));
  const existingBumpItems = items.filter((item) => isBumpItem(item, bumpVariantIds));

  // ── Step 1: clear cart ──────────────────────────────────────────
  if (items.length > 0) {
    const updates = {};
    items.forEach((item) => {
      updates[item.key] = 0;
    });
    await cartUpdate({ updates });
  }

  // ── Step 2: collect sections to bundle ─────────────────────────
  const sectionsToBundle = [];
  document.documentElement.dispatchEvent(
    new CustomEvent('cart:bundled-sections', {
      bubbles: true,
      detail: { sections: sectionsToBundle },
    })
  );

  const allBumpItems = [
    ...existingBumpItems.map((item) => ({
      ...buildLineItemPayload(item),
      properties: { ...item.properties, _order_bump: '1' },
    })),
    { id: newBumpVariantId, quantity: 1, properties: { _order_bump: '1' } },
  ];

  if (normalItems.length > 0) {
    // ── Step 3a: add bump items FIRST (no sections yet) ───────────
    await cartAdd({ items: allBumpItems });

    // ── Step 3b: add normal items AFTER (with sections) ───────────
    return await cartAdd({
      items: normalItems.map(buildLineItemPayload),
      sections: sectionsToBundle,
    });
  }

  // ── Step 3 (no normal items): add bump items with sections ───────
  return await cartAdd({ items: allBumpItems, sections: sectionsToBundle });
}

/* ─────────────────────────────────────────────────────────────────
   OrderBump custom element
───────────────────────────────────────────────────────────────── */
if (!customElements.get('order-bump')) {
  customElements.define(
    'order-bump',
    class OrderBump extends HTMLElement {
      get variantId() {
        return parseInt(this.getAttribute('data-variant-id'));
      }

      get checkbox() {
        return this.querySelector('.order-bump__checkbox');
      }

      get loader() {
        return this.querySelector('.loader');
      }

      connectedCallback() {
        this.checkbox?.addEventListener('change', this.onChange.bind(this));
      }

      onChange(event) {
        if (event.target.checked) {
          this.addToCart();
        } else {
          this.removeFromCart();
        }
      }

      async addToCart() {
        this.enableLoading();
        disableCheckoutButtons();

        try {
          const bumpVariantIds = getOrderBumpVariantIds();
          const addResponse = await rebuildCart(this.variantId, bumpVariantIds);

          if (addResponse.status) {
            // Shopify returned an error
            this.disableLoading();
            enableCheckoutButtons();
            if (this.checkbox) this.checkbox.checked = false;
            return;
          }

          const updatedCart = await fetchCart();
          updatedCart['sections'] = addResponse['sections'] || {};

          if (typeof theme !== 'undefined' && theme.pubsub) {
            theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
              source: 'order-bump',
              cart: updatedCart,
            });
          }
          enableCheckoutButtons();
        } catch (error) {
          console.error('[OrderBump] addToCart error:', error);
          this.disableLoading();
          enableCheckoutButtons();
          if (this.checkbox) this.checkbox.checked = false;
        }
      }

      removeFromCart() {
        this.enableLoading();
        disableCheckoutButtons();

        const sectionsToBundle = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:bundled-sections', {
            bubbles: true,
            detail: { sections: sectionsToBundle },
          })
        );

        fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            updates: { [this.variantId]: 0 },
            sections: sectionsToBundle,
          }),
        })
          .then((r) => r.json())
          .then((parsedState) => {
            if (typeof theme !== 'undefined' && theme.pubsub) {
              theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
                source: 'order-bump',
                cart: parsedState,
              });
            }
            enableCheckoutButtons();
          })
          .catch((error) => {
            console.error(error);
            this.disableLoading();
            enableCheckoutButtons();
            if (this.checkbox) this.checkbox.checked = true;
          });
      }

      enableLoading() {
        if (this.loader) this.loader.hidden = false;
        if (this.checkbox) this.checkbox.disabled = true;
      }

      disableLoading() {
        if (this.loader) this.loader.hidden = true;
        if (this.checkbox) this.checkbox.disabled = false;
      }
    }
  );
}
